/**
 * Custom form section for ``HttpSinkConfig.branches`` (ADR-0008 PR3).
 *
 * Each branch is a hook point on a delivery outcome:
 *   - ``id`` (stable, lowercase + digits + _-) becomes the source_handle
 *     on the canvas + the value of OutboundDelivery.outcome_branch.
 *   - ``label`` is the human display (canvas + action button).
 *   - ``trigger`` decides who can fire it: auto (from outcome rules),
 *     manual (operator click), or both.
 *   - ``linked_workflow_id`` points at a sub-flow. The user does NOT
 *     pick this from a list — sub-flows are built INLINE on the canvas
 *     by clicking the `+` on the branch handle of the sink (PR3b3).
 *     The auto-create flow allocates a hidden WorkflowDefinition with
 *     a delivery_trigger source and wires the id back here.
 *
 * Note: the two implicit branches (``succeeded`` / ``dead_letter``) are
 * always present at runtime and CANNOT be redeclared here. The backend
 * validator rejects that shape; the editor refuses to add either id.
 */
import { ArrowUpRight, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


export type BranchTriggerMode = "auto" | "manual" | "both";

export interface SinkBranch {
  id: string;
  label: string;
  trigger: BranchTriggerMode;
  linked_workflow_id: string | null;
}

// Implicit branch ids — mirror of hub.workflow.catalog.IMPLICIT_BRANCH_IDS.
// Pre-PR3b3 these were REJECTED from `branches[]` (the catalog already
// exposed them as output handles, so a label-only duplicate added nothing).
// PR3b3 relaxed that: the unified canvas needs a slot to store
// linked_workflow_id for implicit branches too (e.g. "run reconciliation
// on every successful delivery"), so we now ACCEPT them here. The unique
// constraint below still catches accidental duplicates.
const IMPLICIT_IDS = new Set(["succeeded", "dead_letter"]);

const TRIGGER_OPTIONS: ComboboxOption[] = [
  {
    value: "auto",
    label: "Auto",
    description: "Fires when an outcome rule routes here.",
  },
  {
    value: "manual",
    label: "Manual",
    description: "Only fires on operator click in the Deliveries page.",
  },
  {
    value: "both",
    label: "Auto + manual",
    description: "Both auto from rules and manual click.",
  },
];

// id must be lowercase + digits + _- per the SinkBranch pydantic regex.
function sanitizeId(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 64);
}

function slugify(label: string): string {
  return sanitizeId(label.replace(/\s+/g, "_"));
}


export interface BranchesEditorProps {
  branches: SinkBranch[];
  onChange: (next: SinkBranch[]) => void;
}


export function BranchesEditor({ branches, onChange }: BranchesEditorProps) {
  // Per-branch validation flags. Surface inline so the operator knows
  // which row will be rejected at save.
  const flagsByIndex = React.useMemo(() => {
    const seenIds = new Map<string, number>();
    return branches.map((b, i) => {
      const flags = {
        reservedId: false, // kept for shape compat; no longer used
        duplicateId: false,
        emptyId: false,
      };
      if (!b.id) {
        flags.emptyId = true;
      } else if (seenIds.has(b.id)) {
        flags.duplicateId = true;
      } else {
        seenIds.set(b.id, i);
      }
      return flags;
    });
  }, [branches]);

  const hasErrors = flagsByIndex.some(
    (f) => f.emptyId || f.reservedId || f.duplicateId,
  );

  function update(idx: number, patch: Partial<SinkBranch>) {
    onChange(
      branches.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    );
  }

  function add() {
    // Pick a sensible default id that doesn't collide. We also
    // implicitly avoid succeeded/dead_letter for the auto-suggest
    // — those need to be added intentionally (the canvas `+` flow
    // does that explicitly when the operator clicks it).
    const used = new Set([...IMPLICIT_IDS, ...branches.map((b) => b.id)]);
    let suffix = branches.length + 1;
    while (used.has(`branch_${suffix}`)) suffix++;
    const id = `branch_${suffix}`;
    onChange([
      ...branches,
      {
        id,
        label: `Branch ${suffix}`,
        trigger: "both",
        linked_workflow_id: null,
      },
    ]);
  }

  function remove(idx: number) {
    if (
      !window.confirm(
        "Remove this branch? Any edge attached to its handle will become orphaned.",
      )
    ) {
      return;
    }
    onChange(branches.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Branches</Label>
          <p className="text-[11px] text-muted-foreground">
            Custom outcome hooks on this sink. The implicit{" "}
            <span className="font-mono">succeeded</span> +{" "}
            <span className="font-mono">dead_letter</span> branches are
            always present. Each branch can link a sub-flow (a workflow
            with a <span className="font-mono">delivery_trigger</span>{" "}
            source).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Add branch
        </Button>
      </div>

      {hasErrors && (
        <Alert tone="warning" title="Some branches have problems">
          Duplicates or empty ids will be rejected at save. Fix the
          rows highlighted below.
        </Alert>
      )}

      <div className="space-y-2">
        {branches.map((b, idx) => (
          <BranchRow
            key={idx}
            branch={b}
            idx={idx}
            flags={flagsByIndex[idx]}
            onChange={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
          />
        ))}
        {branches.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
            No custom branches yet. The sink still has{" "}
            <span className="font-mono">succeeded</span> and{" "}
            <span className="font-mono">dead_letter</span> automatically.
          </div>
        )}
      </div>
    </div>
  );
}


function BranchRow({
  branch,
  idx,
  flags,
  onChange,
  onRemove,
}: {
  branch: SinkBranch;
  idx: number;
  flags: {
    reservedId: boolean; // kept for shape compat — always false in PR3b3+
    duplicateId: boolean;
    emptyId: boolean;
  };
  onChange: (patch: Partial<SinkBranch>) => void;
  onRemove: () => void;
}) {
  const idError = flags.emptyId
    ? "id is required"
    : flags.duplicateId
      ? "duplicate id"
      : null;

  return (
    <div
      className={`rounded-md border p-2 space-y-2 ${
        idError
          ? "border-danger/60 bg-danger/5"
          : "border-border/60 bg-muted/30"
      }`}
    >
      <div className="flex items-center gap-1">
        <span className="font-mono text-[10px] text-muted-foreground">
          #{idx + 1}
        </span>

        <div className="ml-1 flex-1">
          <Input
            value={branch.label}
            onChange={(e) => onChange({ label: e.target.value.slice(0, 128) })}
            onBlur={() => {
              // Convenience: if id is empty + label has content, derive
              // an id from the label so operators don't have to maintain
              // both manually. Honour anything the user already typed.
              if (!branch.id && branch.label) {
                const derived = slugify(branch.label);
                if (derived && !IMPLICIT_IDS.has(derived)) {
                  onChange({ id: derived });
                }
              }
            }}
            placeholder="Label (e.g. Sblocca documento)"
            className="h-7 text-xs"
          />
        </div>

        <div className="w-40">
          <Input
            value={branch.id}
            onChange={(e) => onChange({ id: sanitizeId(e.target.value) })}
            placeholder="id (e.g. sblocca)"
            className={`h-7 font-mono text-[10px] ${
              idError ? "border-danger" : ""
            }`}
            aria-invalid={Boolean(idError)}
            title={idError || "Branch id — lowercase, digits, _ -"}
          />
        </div>

        <div className="w-44">
          <Combobox
            options={TRIGGER_OPTIONS}
            value={branch.trigger}
            onChange={(v) =>
              onChange({ trigger: (v ?? "both") as BranchTriggerMode })
            }
            placeholder="Trigger"
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label="Remove branch"
        >
          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
        </Button>
      </div>

      {idError && (
        <p className="pl-5 text-[10px] text-danger">{idError}</p>
      )}

      <div className="flex items-center gap-2">
        <Label className="text-[11px] text-muted-foreground shrink-0">
          Sub-flow:
        </Label>
        <div className="flex-1">
          {branch.linked_workflow_id ? (
            <div className="flex items-center gap-2 rounded border border-border/60 bg-background px-2 py-1">
              <ArrowUpRight
                className="h-3 w-3 text-muted-foreground"
                strokeWidth={1.5}
              />
              <span className="font-mono text-[10px] text-muted-foreground">
                {branch.linked_workflow_id.slice(0, 8)}…
              </span>
              <span className="text-[11px] text-foreground">
                sub-flow attached
              </span>
              {/* Operator can unlink — useful while iterating. The
                  spawned sub-flow stays as an orphan WorkflowDefinition
                  in the system until manual cleanup. */}
              <button
                type="button"
                className="ml-auto text-[10px] text-muted-foreground hover:text-danger"
                onClick={() => onChange({ linked_workflow_id: null })}
              >
                Unlink
              </button>
            </div>
          ) : (
            <div className="rounded border border-dashed border-border/60 bg-muted/20 px-2 py-1.5 text-[11px] text-muted-foreground">
              No sub-flow yet — click{" "}
              <span className="font-mono text-foreground">+</span> on the{" "}
              <span className="font-mono">{branch.id || "branch"}</span>{" "}
              handle in the canvas to build one inline.
            </div>
          )}
        </div>
      </div>
      <p className="pl-5 text-[10px] text-muted-foreground">
        The sub-flow is a separate workflow with a{" "}
        <span className="font-mono">delivery_trigger</span> source — it
        runs independently when this branch fires (auto on
        classification, or manual from the Deliveries page).
      </p>
    </div>
  );
}
